import {useState, useEffect, useRef, useCallback} from 'react';
import type {VirtualizedDataTableAPI} from '../../../../../components/virtualized-data-table';

interface UseProviderTableLayoutParams {
    visibleColumns: string[];
    modalOpen: boolean;
    sortColumn: string;
    sortDirection: string;
}

interface UseProviderTableLayoutReturn {
    theadHeight: number;
    tableWrapperRef: React.RefObject<HTMLDivElement>;
    cacheKey: number;
    onVirtualizedTableReady: (api: VirtualizedDataTableAPI) => void;
}

/**
 * Hook for provider table-specific layout concerns
 * Handles: thead height measurement, column visibility changes, modal state, sorting
 */
export function useProviderTableLayout({
    visibleColumns,
    modalOpen,
    sortColumn,
    sortDirection
}: UseProviderTableLayoutParams): UseProviderTableLayoutReturn {
    const [theadHeight, setTheadHeight] = useState(48);
    const [cacheKey, setCacheKey] = useState(0);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const virtualizedTableAPIRef = useRef<VirtualizedDataTableAPI | null>(null);

    // Callback to receive VirtualizedDataTable API
    const onVirtualizedTableReady = useCallback((api: VirtualizedDataTableAPI) => {
        virtualizedTableAPIRef.current = api;
    }, []);

    // Measure thead height dynamically using ResizeObserver
    // Catches: column changes, zoom, font loading, CSS changes
    useEffect(() => {
        if (!tableWrapperRef.current) {
            return;
        }

        const thead = tableWrapperRef.current.querySelector('thead');
        if (!thead) {
            return;
        }

        const measureTheadHeight = () => {
            const height = thead.offsetHeight;
            setTheadHeight(height);
        };

        // Measure immediately
        measureTheadHeight();

        // Observe thead for size changes
        const resizeObserver = new ResizeObserver(() => {
            measureTheadHeight();
        });

        resizeObserver.observe(thead);

        return () => {
            resizeObserver.disconnect();
        };
    }, [visibleColumns]);

    // Increment cacheKey when visible columns change (forces VirtualizedDataTable to remeasure)
    useEffect(() => {
        setCacheKey(prev => prev + 1);
    }, [visibleColumns]);

    // When modal closes, remeasure due to scrollbar appearing/disappearing
    useEffect(() => {
        if (!modalOpen && virtualizedTableAPIRef.current) {
            setTimeout(() => {
                virtualizedTableAPIRef.current?.remeasure();
            }, 100);
        }
    }, [modalOpen]);

    // Remeasure when sorting changes
    useEffect(() => {
        if (virtualizedTableAPIRef.current) {
            virtualizedTableAPIRef.current.remeasure();
        }
    }, [sortColumn, sortDirection]);

    return {
        theadHeight,
        tableWrapperRef,
        cacheKey,
        onVirtualizedTableReady
    };
}
